import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { isValidIpAddress } from "@src/cloud-connect/utils/is-valid-ip-address";
import { isValidHostnameOrIpAddress } from "@src/cloud-connect/utils/is-valid-hostname-or-ip-address";
import { RootState } from "@src/reducers/store";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import {
  resetOnboardingState,
  setConnectDataSourceClientCertificate,
  setMachineAvailabilityError,
  setGatewayDetails,
  setGatewayOnboardingStatus,
  setMachineAvailabilityStatus,
  setMachineDetails,
  setMachineSkillsLabel,
  setMachineOnboardingStatus,
  setMachineOnboardingSummary,
} from "@src/reducers/onboarding-slice";
import {
  GatewayOnboardingStatus,
  GatewayOperationError,
  MachineAvailabilityStatus,
  MachineOnboardingStatus,
  MachineOnboardingSummary,
} from "@src/types/gateway-onboarding";
import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { OnboardingStepStatus } from "@src/components/atoms/new-onboarding-stepper/new-onboarding-stepper";
import { checkMachineAvailabilityUseCase } from "@src/cloud-connect/client-configuration/use-cases/check-machine-availability-use-case";
import { Help } from "@src/cloud-connect/help/entities/help";
import { HelpRepository } from "@src/cloud-connect/help/repositories/help-repository";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { CreateSkillRequestBody, SkillType } from "@src/types/skill-types";
import { postCreateSkillUseCase } from "@src/cloud-connect/skills/use-cases/post-create-skill-use-case";
import { createMachineForMachineOnboardingUseCase } from "@src/cloud-connect/client-configuration/use-cases/create-machine-for-machine-onboarding-use-case";
import { generateOpcUaClientCertificateUseCase } from "@src/cloud-connect/client-configuration/use-cases/generate-opcua-client-certificate-use-case";
import {
  DetectedMachineSkill,
  IS_OPC_UA_SKILL_MANDATORY,
  extractGatewayOperationError,
} from "@src/cloud-connect/utils/onboarding-operation";
import {
  buildSkillPayload,
  isValidPort,
  pollMachineAvailabilityOperation,
} from "./use-add-new-machine-drawer-modal-flow";
import {
  createDefaultOpcUaSettings,
  getMachineDrawerTexts,
  getMachineSkillsTexts,
  getPrefillGateway,
} from "./use-add-new-machine-drawer-modal-texts";

export const useAddNewMachineDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const connectedGatewaysListData = useSelector(
    (state: RootState) => state.yourSlice.connectedGatewaysListData,
  );

  const onboardingGateway = useSelector(
    (state: RootState) => state.onboardingSlice.onboardingDetails.gateway,
  );

  const machineAvailabilityStatus = useSelector(
    (state: RootState) => state.onboardingSlice.machineAvailabilityStatus,
  );

  const machineAvailabilityError = useSelector(
    (state: RootState) => state.onboardingSlice.machineAvailabilityError,
  );

  const machineSkillsLabel = useSelector(
    (state: RootState) => state.onboardingSlice.machineSkillsLabel,
  );

  const machineOnboardingStatus = useSelector(
    (state: RootState) => state.onboardingSlice.machineOnboardingStatus,
  );

  const machineOnboardingSummary = useSelector(
    (state: RootState) => state.onboardingSlice.machineOnboardingSummary,
  );

  const gatewayOnboardingStatus = useSelector(
    (state: RootState) => state.onboardingSlice.gatewayOnboardingStatus,
  );
  const completedGatewayIdRef = useRef(
    gatewayOnboardingStatus ===
      GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED
      ? onboardingGateway.gateway_id
      : undefined,
  );

  const onboardingMachine = useSelector(
    (state: RootState) => state.onboardingSlice.onboardingDetails.machine,
  );

  const connectDataSourceClientCertificate = useSelector(
    (state: RootState) =>
      state.onboardingSlice.connectDataSourceClientCertificate,
  );

  const prefillGateway = useMemo(
    (): GatewayWithSourceViewStateTypes | null =>
      getPrefillGateway(onboardingGateway, connectedGatewaysListData),
    [connectedGatewaysListData, onboardingGateway],
  );
  const [selectedGateway, setSelectedGateway] =
    useState<GatewayWithSourceViewStateTypes | null>(prefillGateway);
  const [machineName, setMachineName] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [createdMachineId, setCreatedMachineId] = useState("");
  const [createdMachineHostname, setCreatedMachineHostname] = useState<
    string | null
  >(null);
  const [isMachineCreated, setIsMachineCreated] = useState(false);
  const [areActionsLocked, setAreActionsLocked] = useState(false);
  const availabilityRequestGenerationRef = useRef(0);
  const machineFlowGenerationRef = useRef(0);
  const machineOperationAbortControllerRef = useRef<AbortController | null>(
    null,
  );
  const [detectedMachineSkills, setDetectedMachineSkills] = useState<
    DetectedMachineSkill[]
  >([]);
  const [isSupportExpanded, setIsSupportExpanded] = useState(false);
  const [isTwinExpanded, setIsTwinExpanded] = useState(false);
  const [vncPort, setVncPort] = useState("");
  const [selectedTwinType, setSelectedTwinType] = useState<
    "OPCUA" | "RPC" | null
  >(null);
  const [twinPort, setTwinPort] = useState("");
  const [rpcDsn, setRpcDsn] = useState("");
  const [isInstallSkillsLoading, setIsInstallSkillsLoading] = useState(false);
  const [showProtocolDetails, setShowProtocolDetails] = useState(false);
  const [machineOperationFailureDetails, setMachineOperationFailureDetails] =
    useState<GatewayOperationError | null>(null);
  const [hasNoSkillsIdentified, setHasNoSkillsIdentified] = useState(false);
  const [isAutoGenerateEnabled, setIsAutoGenerateEnabled] = useState(true);
  const [displayUserLoginPasswordAsText, setDisplayUserLoginPasswordAsText] =
    useState(false);
  const [opcUaSettings, setOpcUaSettings] = useState(
    createDefaultOpcUaSettings(),
  );

  const texts = getMachineDrawerTexts(i18n);
  const machineSkillsTexts = getMachineSkillsTexts(i18n);

  const isMachineSummaryStep = machineOnboardingSummary.status !== "IDLE";
  const isMachineSkillsStep =
    !isMachineSummaryStep &&
    machineOnboardingStatus ===
      MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED;

  const isMachineDetailsCompleted =
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS ||
    isMachineSkillsStep ||
    isMachineSummaryStep;

  const isThingsOsStepCompleted = isMachineSkillsStep || isMachineSummaryStep;

  const steps: { label: string; status: OnboardingStepStatus }[] =
    isMachineDetailsCompleted
      ? [
          { label: texts.stepGateway, status: "completed" },
          { label: texts.stepMachine, status: "completed" },
          {
            label: texts.stepThingsOs,
            status: isThingsOsStepCompleted ? "completed" : "pending",
          },
        ]
      : [
          { label: texts.stepGateway, status: "completed" },
          { label: texts.stepMachine, status: "active" },
          { label: texts.stepThingsOs, status: "pending" },
        ];

  const title =
    isMachineSkillsStep || isMachineSummaryStep
      ? texts.titleStepTwo
      : texts.title;
  const sectionTitle = isMachineSummaryStep
    ? texts.sectionTitleSummary
    : isMachineSkillsStep
    ? texts.sectionTitleStepTwo
    : texts.sectionTitle;

  const isConnectDisabled =
    !selectedGateway || !machineName.trim() || !ipAddress.trim();

  const showConnectButton =
    !areActionsLocked &&
    machineAvailabilityStatus === MachineAvailabilityStatus.IDLE;

  const showContinueButton =
    !areActionsLocked &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.IDLE &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.FAILED &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.WARNING;

  const showFinishWithoutSkillsButton =
    !areActionsLocked &&
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS;

  const hasOpcUaSkill = detectedMachineSkills.some(
    (skill) => skill.type === "OPCUA",
  );
  const hasAllExpectedMachineSkills = ["VNC", "OPCUA"].every((skillType) =>
    detectedMachineSkills.some((skill) => skill.type === skillType),
  );

  // Detection no longer gates the journey: the user may continue even when no
  // skill (or no OPC UA) was identified, and configures OPC UA manually.
  const isContinueButtonEnabled =
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS;

  const isInteractionLocked =
    isInstallSkillsLoading ||
    machineAvailabilityStatus === MachineAvailabilityStatus.LOADING ||
    machineAvailabilityStatus === MachineAvailabilityStatus.IP_REACHABLE;

  const hasStoredGeneratedCertificate = Boolean(
    connectDataSourceClientCertificate,
  );

  const showReuseGeneratedCertificateHint =
    opcUaSettings.authOption === DeviceAuthOption.CLIENT_CERTIFICATE &&
    isAutoGenerateEnabled &&
    hasStoredGeneratedCertificate;

  // Both skill cards are always offered: detection only prefills ports, it no
  // longer decides which skills the user may install.
  const twinTypeTabs = useMemo((): Array<"OPCUA" | "RPC"> => {
    const uniqueDetectedTypes = detectedMachineSkills
      .map((skill) => skill.type)
      .filter(
        (type, index, all): type is "OPCUA" | "RPC" =>
          (type === "OPCUA" || type === "RPC") && all.indexOf(type) === index,
      );

    if (!IS_OPC_UA_SKILL_MANDATORY || uniqueDetectedTypes.includes("OPCUA")) {
      return uniqueDetectedTypes;
    }

    return ["OPCUA", ...uniqueDetectedTypes];
  }, [detectedMachineSkills]);
  const hasTwinSkill = twinTypeTabs.length > 0;

  const getDetectedPortForType = useCallback(
    (type: SkillType): string => {
      const detectedSkill = detectedMachineSkills.find(
        (skill) => skill.type === type,
      );
      if (!detectedSkill?.port) {
        return "";
      }

      return `${detectedSkill.port}`;
    },
    [detectedMachineSkills],
  );

  const hasDefaultDetectedPort = (type: SkillType, portValue: string) => {
    const detectedPort = getDetectedPortForType(type);
    return Boolean(detectedPort) && portValue.trim() === detectedPort;
  };

  const showVncPortAvailable =
    isValidPort(vncPort) && hasDefaultDetectedPort("VNC", vncPort);

  useEffect(() => {
    if (isMachineSkillsStep && showVncPortAvailable) {
      setIsSupportExpanded(true);
    }
  }, [isMachineSkillsStep, showVncPortAvailable]);

  const showTwinPortAvailable =
    selectedTwinType === "OPCUA" &&
    isValidPort(twinPort) &&
    hasDefaultDetectedPort("OPCUA", twinPort);

  const resetMachineAvailabilityState = useCallback((): void => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    availabilityRequestGenerationRef.current += 1;
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.IDLE));
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineSkillsLabel(""));
    dispatch(
      setMachineOnboardingSummary({
        status: "IDLE",
        installedSkills: [],
        failedSkills: [],
        skippedSkills: [],
      }),
    );
    setCreatedMachineId("");
    setCreatedMachineHostname(null);
    setIsMachineCreated(false);
    setAreActionsLocked(false);
    setShowProtocolDetails(false);
    setMachineOperationFailureDetails(null);
    setHasNoSkillsIdentified(false);
    dispatch(setMachineDetails({}));
    dispatch(setConnectDataSourceClientCertificate(null));
  }, [dispatch]);

  useEffect(() => {
    resetMachineAvailabilityState();
    dispatch(
      setMachineOnboardingStatus(MachineOnboardingStatus.MACHINE_NOT_STARTED),
    );
    setDetectedMachineSkills([]);
    setIsSupportExpanded(false);
    setIsTwinExpanded(false);
  }, [dispatch, resetMachineAvailabilityState]);

  useEffect(() => {
    if (!prefillGateway) {
      setSelectedGateway(null);
      return;
    }

    setSelectedGateway((previousGateway) => {
      if (previousGateway?.gateway_uuid === prefillGateway.gateway_uuid) {
        return previousGateway;
      }
      return prefillGateway;
    });
  }, [prefillGateway]);

  useEffect(() => {
    if (!isMachineSkillsStep) {
      return;
    }

    // TODO: Temporary OPC UA compatibility change. Remove this forced default
    // once RPC/non-OPC UA onboarding is supported again.
    const initialTwinType = twinTypeTabs.includes("OPCUA")
      ? "OPCUA"
      : twinTypeTabs[0] ?? null;

    setSelectedTwinType((previousType) =>
      previousType === "RPC" && initialTwinType === "OPCUA"
        ? initialTwinType
        : previousType || initialTwinType,
    );

    if (initialTwinType === "OPCUA") {
      setIsTwinExpanded(true);
    }

    const detectedVncPort = getDetectedPortForType("VNC");
    setVncPort((previousPort) => previousPort || detectedVncPort);

    if (initialTwinType) {
      const detectedTwinPort = getDetectedPortForType(initialTwinType);
      setTwinPort((previousPort) => previousPort || detectedTwinPort);
    }
  }, [isMachineSkillsStep, twinTypeTabs, getDetectedPortForType]);

  const onSelectGateway = (
    gateway: GatewayWithSourceViewStateTypes | null,
  ): void => {
    availabilityRequestGenerationRef.current += 1;
    setSelectedGateway(gateway);
    resetMachineAvailabilityState();
  };

  const onChangeMachineName = (value: string): void => {
    availabilityRequestGenerationRef.current += 1;
    setMachineName(value);
    resetMachineAvailabilityState();
  };

  const onChangeIpAddress = (value: string): void => {
    const normalizedValue = value.replace(/\s/g, "");
    if (normalizedValue === ipAddress) {
      return;
    }

    availabilityRequestGenerationRef.current += 1;
    setIpAddress(normalizedValue);
    resetMachineAvailabilityState();
  };

  const runMachineAvailabilityCheck = async (): Promise<void> => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    const requestGeneration = ++availabilityRequestGenerationRef.current;
    const trimmedMachineName = machineName.trim();
    const trimmedIpAddress = ipAddress.trim();

    if (!isValidHostnameOrIpAddress(trimmedIpAddress)) {
      warningSnackbar(texts.invalidIpAddressErrorMessage);
      return;
    }

    const failMachineAvailability = (
      error: string,
      details?: GatewayOperationError,
    ) => {
      setAreActionsLocked(false);
      setMachineOperationFailureDetails(details || null);
      dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.FAILED));
      dispatch(setMachineAvailabilityError(error));
    };

    setAreActionsLocked(false);
    setCreatedMachineId("");
    setCreatedMachineHostname(null);
    setIsMachineCreated(false);
    setDetectedMachineSkills([]);
    dispatch(setMachineDetails({}));
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineSkillsLabel(""));
    setMachineOperationFailureDetails(null);
    setShowProtocolDetails(false);
    setHasNoSkillsIdentified(false);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.LOADING));

    if (!selectedGateway?.gateway_uuid) {
      failMachineAvailability(texts.machineIpAddressMismatchErrorMessage);
      return;
    }

    try {
      const machineAvailabilityResult = await checkMachineAvailabilityUseCase(
        selectedGateway.gateway_uuid,
        trimmedIpAddress,
      );

      if (requestGeneration !== availabilityRequestGenerationRef.current) {
        return;
      }

      const operationId = machineAvailabilityResult.data?.operation_id;
      const machineId = machineAvailabilityResult.data?.machine_id;

      if (!machineAvailabilityResult.success || !operationId) {
        const details = extractGatewayOperationError(
          machineAvailabilityResult.errorData || machineAvailabilityResult.data,
        );

        failMachineAvailability(
          machineAvailabilityResult.error ||
            details?.title ||
            details?.detail ||
            machineAvailabilityResult.data?.detail ||
            machineAvailabilityResult.data?.message ||
            texts.machineIpAddressMismatchErrorMessage,
          details,
        );
        return;
      }

      const operationAbortController = new AbortController();
      machineOperationAbortControllerRef.current = operationAbortController;

      dispatch(
        setMachineAvailabilityStatus(MachineAvailabilityStatus.IP_REACHABLE),
      );

      if (machineId) {
        setCreatedMachineId(machineId);
        dispatch(
          setMachineDetails({
            machine_id: machineId,
            name: trimmedMachineName,
            hostname: trimmedIpAddress,
          }),
        );
      }

      const machineSkillsResult = await pollMachineAvailabilityOperation(
        operationId,
        texts,
        machineOperationAbortControllerRef.current?.signal,
      );

      if (requestGeneration !== availabilityRequestGenerationRef.current) {
        return;
      }

      if (machineSkillsResult.success === false) {
        setAreActionsLocked(false);
        setMachineOperationFailureDetails(machineSkillsResult.details || null);
        dispatch(
          setMachineAvailabilityStatus(
            machineSkillsResult.isWarning
              ? MachineAvailabilityStatus.WARNING
              : MachineAvailabilityStatus.FAILED,
          ),
        );
        dispatch(
          setMachineAvailabilityError(
            machineSkillsResult.error || texts.skillsNotAvailableErrorMessage,
          ),
        );
        return;
      }

      setDetectedMachineSkills(machineSkillsResult.skills);
      setHasNoSkillsIdentified(machineSkillsResult.skills.length === 0);
      dispatch(
        setMachineSkillsLabel(
          machineSkillsResult.skills.map((skill) => skill.type).join(", "),
        ),
      );
      dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.SUCCESS));
    } catch {
      if (requestGeneration === availabilityRequestGenerationRef.current) {
        failMachineAvailability(texts.machineIpAddressMismatchErrorMessage);
      }
    }
  };

  const handleConnectClick = (e: MouseEvent | KeyboardEvent): Promise<void> => {
    e.preventDefault();

    return runMachineAvailabilityCheck();
  };

  const handleRetry = async (): Promise<void> => {
    // Retry the availability request itself so the backend performs a fresh
    // reachability and skill-detection check before we poll its operation.
    return runMachineAvailabilityCheck();
  };

  const handleContinueToMachineSkills = (
    e: MouseEvent | KeyboardEvent,
  ): void => {
    e.preventDefault();

    const trimmedMachineName = machineName.trim();
    const trimmedIpAddress = ipAddress.trim();

    if (!isContinueButtonEnabled) {
      return;
    }

    if (selectedGateway) {
      dispatch(
        setGatewayDetails({
          gateway_id: selectedGateway.gateway_uuid,
          name: selectedGateway.name,
        }),
      );
    }

    dispatch(
      setMachineDetails({
        machine_id: createdMachineId || undefined,
        name: trimmedMachineName,
        hostname: trimmedIpAddress,
      }),
    );
    dispatch(
      setMachineOnboardingStatus(
        MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED,
      ),
    );
  };

  const toggleAutoGenerateCertificate = (): void => {
    setIsAutoGenerateEnabled((previousState) => {
      const nextState = !previousState;
      setOpcUaSettings((previousSettings) => ({
        ...previousSettings,
        reuseLastAutogeneratedClientCertificate: nextState,
      }));

      return nextState;
    });
  };

  const handlePasswordButton = (e: MouseEvent | KeyboardEvent): void => {
    e.preventDefault();
    setDisplayUserLoginPasswordAsText(!displayUserLoginPasswordAsText);
  };

  const clearOnboardingAfterMachineCompletion = (): void => {
    dispatch(resetOnboardingState());
  };

  const failMachineFlow = (
    error: string,
    skillTypes: string[] = [],
    details?: GatewayOperationError,
  ) => {
    setAreActionsLocked(true);
    setMachineOperationFailureDetails(details || null);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.FAILED));
    dispatch(setMachineAvailabilityError(error));

    if (
      machineOnboardingStatus ===
      MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED
    ) {
      // Without a failed entry the summary would render neither the failure
      // message nor the protocol toggle, so an unspecific failure is reported
      // against the machine itself.
      const failedTypes = skillTypes.length > 0 ? skillTypes : ["MACHINE"];

      dispatch(
        setMachineOnboardingSummary({
          status: "FAILED",
          installedSkills: [],
          failedSkills: failedTypes.map((type) => ({ type, error, details })),
          skippedSkills: [],
        }),
      );
    }
  };

  /** Clears a previous machine-creation failure before a new install attempt. */
  const clearMachineFailureState = (): void => {
    setAreActionsLocked(false);
    setMachineOperationFailureDetails(null);
    setShowProtocolDetails(false);
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.SUCCESS));
  };

  const failMachineCreation = (
    error: string,
    details?: GatewayOperationError,
  ): void => {
    setMachineOperationFailureDetails(details || null);
    setShowProtocolDetails(false);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.FAILED));
    dispatch(setMachineAvailabilityError(error));
  };

  const createOnboardedMachine = async (
    activeMachineFlowGeneration?: number,
  ): Promise<{
    machineId: string;
    hostname: string | null;
  } | null> => {
    if (isMachineCreated && createdMachineId) {
      return { machineId: createdMachineId, hostname: createdMachineHostname };
    }

    if (!selectedGateway?.gateway_uuid) {
      const error = texts.createMachineFailedErrorMessage;
      errorSnackbar(error);
      failMachineCreation(error);
      return null;
    }

    const createMachineResult = await createMachineForMachineOnboardingUseCase({
      gateway_id: selectedGateway.gateway_uuid,
      machine_id: createdMachineId || undefined,
      name: machineName.trim(),
      hostname: ipAddress.trim(),
      serial_number: null,
    });

    if (
      activeMachineFlowGeneration !== undefined &&
      machineFlowGenerationRef.current !== activeMachineFlowGeneration
    ) {
      return null;
    }

    if (!createMachineResult.success) {
      const details = extractGatewayOperationError(
        createMachineResult.errorData,
      );
      const error =
        createMachineResult.error ||
        details?.title ||
        details?.detail ||
        texts.createMachineFailedErrorMessage;
      errorSnackbar(error);
      failMachineCreation(error, details);
      return null;
    }

    const machineId = createMachineResult.data?.id || createdMachineId;

    if (!machineId) {
      const error = texts.createMachineFailedErrorMessage;
      errorSnackbar(error);
      failMachineCreation(error);
      return null;
    }

    const hostname = createMachineResult.data?.hostname || null;

    setCreatedMachineId(machineId);
    setCreatedMachineHostname(hostname);
    setIsMachineCreated(true);
    dispatch(
      setMachineDetails({
        machine_id: machineId,
        name: machineName.trim(),
        hostname: ipAddress.trim(),
      }),
    );

    return { machineId, hostname };
  };

  // The generated certificate is only valid for the client-certificate
  // auto-generate context it was created in. Editing port or security
  // settings keeps it; switching auth option or disabling auto-generate
  // invalidates it (next install generates a new one).
  useEffect(() => {
    if (!isAutoGenerateEnabled) {
      dispatch(setConnectDataSourceClientCertificate(null));
    }
  }, [isAutoGenerateEnabled, dispatch]);

  const handleInstallAndFinishSetup = async (): Promise<void> => {
    const hostName = ipAddress.trim();
    const payloads: CreateSkillRequestBody[] = [];
    const isOpcUaSelected =
      isTwinExpanded && hasTwinSkill && selectedTwinType === "OPCUA";
    const useAutoGeneratedClientCertificate =
      isOpcUaSelected && isAutoGenerateEnabled;

    // OPC UA is mandatory for now: the setup can only be finished with it.
    if (IS_OPC_UA_SKILL_MANDATORY && !isOpcUaSelected) {
      warningSnackbar(texts.opcUaSkillRequiredErrorMessage);
      return;
    }

    if (isSupportExpanded) {
      if (!isValidPort(vncPort)) {
        warningSnackbar(texts.invalidPortErrorMessage);
        return;
      }

      payloads.push(
        buildSkillPayload("VNC", hostName, opcUaSettings, Number(vncPort)),
      );
    }

    if (isTwinExpanded && hasTwinSkill && selectedTwinType) {
      if (selectedTwinType === "OPCUA") {
        if (!isValidPort(twinPort)) {
          warningSnackbar(texts.invalidPortErrorMessage);
          return;
        }

        if (
          opcUaSettings.authOption === DeviceAuthOption.CLIENT_CERTIFICATE &&
          !isAutoGenerateEnabled
        ) {
          const missingFields: string[] = [];
          if (!opcUaSettings.clientCertificateBase64) {
            missingFields.push(machineSkillsTexts.clientCertificateLabel);
          }
          if (!opcUaSettings.clientCertificatePrivateKeyBase64) {
            missingFields.push(
              machineSkillsTexts.clientCertificatePrivateKeyLabel,
            );
          }

          if (missingFields.length > 0) {
            warningSnackbar(
              `${machineSkillsTexts.pleaseFixFieldsLabel}: ${missingFields.join(
                ", ",
              )}`,
              5000,
            );
            return;
          }
        }

        // The OPCUA payload is built after machine creation, once the
        // client certificate (generated or stored) is available.
      } else {
        // Unreachable while IS_OPC_UA_SKILL_MANDATORY is true.
        const trimmedRpcDsn = rpcDsn.trim();
        if (!isValidIpAddress(trimmedRpcDsn)) {
          warningSnackbar(texts.invalidRpcDsnErrorMessage);
          return;
        }

        payloads.push(
          buildSkillPayload(
            "RPC",
            hostName,
            opcUaSettings,
            undefined,
            trimmedRpcDsn,
          ),
        );
      }
    }

    clearMachineFailureState();

    const selectedSkillTypes: string[] = [
      ...(isSupportExpanded ? ["VNC"] : []),
      ...(isTwinExpanded && hasTwinSkill && selectedTwinType
        ? [selectedTwinType]
        : []),
    ];

    const activeMachineFlowGeneration = ++machineFlowGenerationRef.current;
    setIsInstallSkillsLoading(true);

    try {
      const createdMachine = await createOnboardedMachine(
        activeMachineFlowGeneration,
      );

      if (
        machineFlowGenerationRef.current !== activeMachineFlowGeneration ||
        !createdMachine
      ) {
        return;
      }

      const { machineId, hostname } = createdMachine;

      if (isOpcUaSelected) {
        let generatedCertificate = useAutoGeneratedClientCertificate
          ? connectDataSourceClientCertificate
          : null;

        if (useAutoGeneratedClientCertificate && !generatedCertificate) {
          const certificateResult = await generateOpcUaClientCertificateUseCase(
            hostname ? [hostname] : [],
          );

          if (
            machineFlowGenerationRef.current !== activeMachineFlowGeneration
          ) {
            return;
          }

          if (
            !certificateResult.success ||
            !certificateResult.data?.clientCertificateBase64.trim() ||
            !certificateResult.data.clientCertificatePrivateKeyBase64.trim()
          ) {
            const certificateError =
              certificateResult.error ||
              texts.certificateGenerationFailedErrorMessage;
            errorSnackbar(certificateError);
            failMachineFlow(
              certificateError,
              selectedSkillTypes,
              extractGatewayOperationError(certificateResult.errorData),
            );
            return;
          }

          generatedCertificate = certificateResult.data;
          dispatch(setConnectDataSourceClientCertificate(generatedCertificate));
        }

        const effectiveOpcUaSettings = generatedCertificate
          ? {
              ...opcUaSettings,
              clientCertificateBase64:
                generatedCertificate.clientCertificateBase64,
              clientCertificatePrivateKeyBase64:
                generatedCertificate.clientCertificatePrivateKeyBase64,
              clientCertificatePassword:
                generatedCertificate.clientCertificatePassword ?? "",
            }
          : opcUaSettings;

        payloads.push(
          buildSkillPayload(
            "OPCUA",
            hostName,
            effectiveOpcUaSettings,
            Number(twinPort),
            undefined,
            Boolean(generatedCertificate),
          ),
        );
      }

      const skillOperationAbortController = new AbortController();
      machineOperationAbortControllerRef.current?.abort();
      machineOperationAbortControllerRef.current =
        skillOperationAbortController;
      const createSkillResult = await postCreateSkillUseCase(
        payloads,
        machineId,
        texts.operationPendingWarningMessage,
        skillOperationAbortController.signal,
      );

      if (machineFlowGenerationRef.current !== activeMachineFlowGeneration) {
        return;
      }

      if (!createSkillResult.success) {
        errorSnackbar(
          createSkillResult.error || texts.skillsNotAvailableErrorMessage,
        );
        failMachineFlow(
          createSkillResult.error || texts.skillsNotAvailableErrorMessage,
          selectedSkillTypes,
          createSkillResult.errorDetails,
        );
        return;
      }

      const successfulSkillTypes = createSkillResult.successfulSkillTypes || [];
      const opcUaOperation =
        createSkillResult.operationResultsBySkillType?.OPCUA;
      const machineSourceId = successfulSkillTypes.includes("OPCUA")
        ? opcUaOperation?.result?.source_id || opcUaOperation?.source_id
        : undefined;

      if (machineSourceId) {
        dispatch(
          setMachineDetails({
            machine_id: machineId,
            source_id: machineSourceId,
            name: machineName,
            hostname: hostName,
          }),
        );
      }

      const installedSkills = selectedSkillTypes.filter((type) =>
        successfulSkillTypes.includes(type),
      );

      const failedSkills: MachineOnboardingSummary["failedSkills"] =
        createSkillResult.failedSkillTypes &&
        createSkillResult.failedSkillTypes.length > 0
          ? createSkillResult.failedSkillTypes
          : selectedSkillTypes
              .filter((type) => !successfulSkillTypes.includes(type))
              .map((type) => ({
                type,
                error: texts.skillsNotAvailableErrorMessage,
              }));

      if (failedSkills.length > 0) {
        // Keep the operation protocol of the failure available to the summary
        // panel and the availability panel after a retry.
        setMachineOperationFailureDetails(failedSkills[0].details || null);
        dispatch(
          setMachineOnboardingSummary({
            status:
              installedSkills.length > 0 && !createSkillResult.isWarning
                ? "PARTIAL_SUCCESS"
                : "FAILED",
            installedSkills,
            failedSkills,
            skippedSkills: [],
          }),
        );

        errorSnackbar(failedSkills[0].error);
        return;
      }

      dispatch(
        setMachineOnboardingSummary({
          status: "SUCCESS",
          installedSkills,
          failedSkills: [],
          skippedSkills: [],
        }),
      );
      successSnackbar(texts.skillInstallSuccessMessage);
    } catch {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        failMachineFlow(
          texts.skillsNotAvailableErrorMessage,
          selectedSkillTypes,
        );
      }
    } finally {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        setIsInstallSkillsLoading(false);
      }
    }
  };

  const handleFinishWithoutMachineSkills = async (): Promise<void> => {
    const activeMachineFlowGeneration = ++machineFlowGenerationRef.current;
    setIsInstallSkillsLoading(true);

    try {
      const createdMachine = await createOnboardedMachine(
        activeMachineFlowGeneration,
      );
      if (
        machineFlowGenerationRef.current !== activeMachineFlowGeneration ||
        !createdMachine
      ) {
        return;
      }

      dispatch(
        setMachineOnboardingSummary({
          status: "SKIPPED",
          installedSkills: [],
          failedSkills: [],
          skippedSkills: detectedMachineSkills.map((skill) => skill.type),
        }),
      );
    } catch {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        failMachineFlow(texts.createMachineFailedErrorMessage);
      }
    } finally {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        setIsInstallSkillsLoading(false);
      }
    }
  };

  const resetMachineDrawerLocalState = (
    preserveSelectedGateway = false,
  ): void => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    machineFlowGenerationRef.current += 1;
    dispatch(
      setMachineOnboardingStatus(MachineOnboardingStatus.MACHINE_NOT_STARTED),
    );
    resetMachineAvailabilityState();

    setIsInstallSkillsLoading(false);
    if (!preserveSelectedGateway) {
      setSelectedGateway(null);
    }
    setMachineName("");
    setIpAddress("");
    setDetectedMachineSkills([]);
    setIsSupportExpanded(false);
    setIsTwinExpanded(false);
    setVncPort("");
    setTwinPort("");
    setRpcDsn("");
    setSelectedTwinType(null);
    setIsAutoGenerateEnabled(true);
    setDisplayUserLoginPasswordAsText(false);
    setOpcUaSettings(createDefaultOpcUaSettings());
  };

  const handleFinishSummaryStep = (): void => {
    const selectedGatewayMatchesOnboardingGateway =
      selectedGateway?.gateway_uuid === onboardingGateway.gateway_id;
    const gatewayToPreserve = selectedGateway
      ? selectedGatewayMatchesOnboardingGateway
        ? onboardingGateway
        : {
            gateway_id: selectedGateway.gateway_uuid,
            name: selectedGateway.name,
            vendor_id: selectedGateway.vendor_id,
          }
      : onboardingGateway;
    const shouldRestoreCompletedGatewayStatus =
      gatewayOnboardingStatus ===
        GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED &&
      selectedGateway?.gateway_uuid === completedGatewayIdRef.current;

    clearOnboardingAfterMachineCompletion();
    if (gatewayToPreserve.gateway_id) {
      dispatch(setGatewayDetails(gatewayToPreserve));
      if (shouldRestoreCompletedGatewayStatus) {
        dispatch(
          setGatewayOnboardingStatus(
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
          ),
        );
      }
    }
    resetMachineDrawerLocalState(true);
  };

  const handleGoToNewMachine = (): void => {
    const sourceId = onboardingMachine?.source_id;

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addNewMachineDrawerModal",
      }),
    );
    clearOnboardingAfterMachineCompletion();
    resetMachineDrawerLocalState();
    navigateTo(
      sourceId
        ? `${Route.MACHINES}?source_id=${encodeURIComponent(sourceId)}`
        : Route.MACHINES,
    );
  };

  const handleUpdatePortsFromSummary = (): void => {
    setAreActionsLocked(false);
    setShowProtocolDetails(false);
    dispatch(
      setMachineOnboardingSummary({
        status: "IDLE",
        installedSkills: [],
        failedSkills: [],
        skippedSkills: [],
      }),
    );
  };

  const handleShowProtocol = () => {
    setShowProtocolDetails((currentValue) => !currentValue);
  };

  const handleGetSupport = () => {
    const help = new Help(new HelpRepository());
    const gatewayUuid =
      selectedGateway?.gateway_uuid || onboardingGateway?.gateway_id;
    const machineUuid = onboardingMachine?.machine_id || createdMachineId;
    const sourceId = onboardingMachine?.source_id;

    const failedSkillErrors = machineOnboardingSummary.failedSkills
      .map((skill) => `${skill.type}: ${skill.error}`)
      .join("; ");

    window.open(
      help.getContactUrlForMachineOnboarding({
        gatewayName: selectedGateway?.name || onboardingGateway?.name,
        gatewayUuid,
        namespaceName: onboardingGateway?.namespace,
        machineName,
        machineUuid,
        sourceId,
        hostname: ipAddress,
        failureStage: isMachineCreated
          ? "Skill installation"
          : "Machine availability or creation",
        failedSkill:
          machineOnboardingSummary.failedSkills.length > 0
            ? machineOnboardingSummary.failedSkills
                .map((skill) => skill.type)
                .join(", ")
            : undefined,
        errorMessage: failedSkillErrors || machineAvailabilityError,
        errorDetails: machineOperationFailureDetails
          ? {
              "Error code": machineOperationFailureDetails.error_code,
              Title: machineOperationFailureDetails.title,
              Detail: machineOperationFailureDetails.detail,
              Severity: machineOperationFailureDetails.severity,
              "Recommended action":
                machineOperationFailureDetails.recommended_action,
              "Trace ID": machineOperationFailureDetails.trace_id,
            }
          : undefined,
      }),
      "_blank",
    );
  };

  const handleCloseMachineDrawer = (): void => {
    clearOnboardingAfterMachineCompletion();
    resetMachineDrawerLocalState();
  };

  return {
    texts,
    machineSkillsTexts,
    detectedMachineSkillTypes: detectedMachineSkills
      .map((skill) => skill.type)
      .join(", "),
    title,
    sectionTitle,
    steps,
    isMachineSkillsStep,
    isMachineSummaryStep,
    machineOnboardingSummary,
    onboardingMachine,
    connectedGatewaysListData,
    selectedGateway,
    onSelectGateway,
    machineName,
    onChangeMachineName,
    ipAddress,
    onChangeIpAddress,
    isConnectDisabled,
    showConnectButton,
    showContinueButton,
    showFinishWithoutSkillsButton,
    isContinueButtonEnabled,
    machineAvailabilityStatus,
    isInteractionLocked,
    machineSkillsLabel,
    machineAvailabilityError,
    machineOperationFailureDetails,
    hasNoSkillsIdentified,
    hasOpcUaSkill,
    hasAllExpectedMachineSkills,
    showProtocolDetails,
    areActionsLocked,
    hasTwinSkill,
    twinTypeTabs,
    isSupportExpanded,
    isTwinExpanded,
    vncPort,
    twinPort,
    rpcDsn,
    selectedTwinType,
    opcUaSettings,
    isAutoGenerateEnabled,
    showReuseGeneratedCertificateHint,
    hasStoredGeneratedCertificate,
    displayUserLoginPasswordAsText,
    isInstallSkillsLoading,
    showVncPortAvailable,
    showTwinPortAvailable,
    setIsSupportExpanded,
    setIsTwinExpanded,
    setVncPort,
    setTwinPort,
    setRpcDsn,
    setSelectedTwinType,
    setOpcUaSettings,
    toggleAutoGenerateCertificate,
    handlePasswordButton,
    handleConnectClick,
    handleRetry,
    handleContinueToMachineSkills,
    handleInstallAndFinishSetup,
    handleFinishWithoutMachineSkills,
    handleFinishSummaryStep,
    handleGoToNewMachine,
    handleUpdatePortsFromSummary,
    handleShowProtocol,
    handleGetSupport,
    handleCloseMachineDrawer,
    isGatewayPrefilled:
      prefillGateway !== null &&
      gatewayOnboardingStatus ===
        GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
    isMachineDetailsCompleted,
    isThingsOsStepCompleted,
  };
};
